import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { EMPTY_FILTERS, buildFilterModel, filterModelToFilterState } from './partListFilters';
import { parsePartListUrl } from './partListUrlState';
import { updateReportByCode } from '../../../api/report';
import { FilterState } from '../../../components/molecules/FilterBar';
import { SavedReport } from '../../../components/molecules/ReportTabs';
import { ColumnDef, SortModel } from '../../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { SavedReportTypeList } from '../../../configs/enums';
import {
  useAddNewReport,
  useGetDefaultReportByReportType,
  useGetReportByCode,
  useGetSavedReports,
  useUpdateBulkReports,
} from '../../../queries/report-query';
import { generateUniqueCode } from '../../../utils/common';
import { sanitizeSavedReportFilterModel } from '../../../utils/filterModel';
import { toast } from '../../../utils/toast';

export interface UsePartReportsReturn {
  savedReports: SavedReport[];
  activeReport: string | null;
  activeReportName: string | null;
  defaultTabLabel: string;
  saveDialogOpen: boolean;
  handleReportChange: (code: string | null) => void;
  handleSaveNewReport: (name: string, setAsDefault: boolean) => Promise<void>;
  handleOverwriteReport: (setAsDefault: boolean) => Promise<void>;
  handleSetDefault: (report: SavedReport) => Promise<void>;
  setSaveDialogOpen: (open: boolean) => void;
}

interface UsePartReportsOptions {
  filterModel: { items: GridFilterItem[] };
  pageSize: number;
  sortModel: SortModel;
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  applyFilters: (filters: FilterState) => void;
  applyReportConfig: (
    filterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
    columnOrder?: Array<{ field: string }>
  ) => void;
  /** Restore the default view's own column layout (session, else built defaults)
   *  so returning to the default tab doesn't inherit a report's columns. */
  resetColumnsToDefault: () => void;
  setFilters: (filters: FilterState) => void;
  setFilterBarKey: React.Dispatch<React.SetStateAction<number>>;
}

export const usePartReports = ({
  filterModel,
  pageSize,
  sortModel,
  columns,
  columnSelectionData,
  applyFilters,
  applyReportConfig,
  resetColumnsToDefault,
  setFilters,
  setFilterBarKey,
}: UsePartReportsOptions): UsePartReportsReturn => {
  const location = useLocation();

  const urlSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlReportCode = useMemo(() => urlSearchParams.get('savedReportCode'), [urlSearchParams]);
  // Codec-canonical chip / multi-paste state in URL means the user is
  // returning to a previously-applied view — typically via the Back
  // button. Skip the default-report apply so it doesn't trample what we
  // hydrated from URL.
  const hasUrlChipState = useMemo(() => {
    const parsed = parsePartListUrl(urlSearchParams);
    return parsed.hasChipState || parsed.multiSearchQuery !== '';
  }, [urlSearchParams]);
  const [activeReport, setActiveReport] = useState<string | null>(urlReportCode);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  useEffect(() => {
    // Adopt a code from the URL, but never clear the selection when it goes
    // away: `savedReportCode` is a transient key the URL codec strips as soon as
    // the page writes its canonical URL. Treating that removal as "no report"
    // left the report's filters applied while the tab strip snapped back to the
    // default tab.
    //
    // The alternative — keeping the code in the canonical URL and syncing both
    // ways — was rejected: the key is stripped precisely so the report does not
    // re-apply over chip state on every back-button remount (see TRANSIENT_KEYS
    // in this page's url-state module).
    //
    // The accepted trade-off is that a live component can no longer be returned
    // to the default view by the URL alone. Nothing is lost in practice: a fresh
    // mount still seeds activeReport from the URL, so back-navigation to a
    // code-less URL lands on the default, and the default tab clears the report
    // explicitly through handleReportChange(null).
    if (urlReportCode) setActiveReport(urlReportCode);
  }, [urlReportCode]);

  const addNewReport = useAddNewReport();
  const bulkUpdateReports = useUpdateBulkReports();
  const defaultReportQuery = useGetDefaultReportByReportType(SavedReportTypeList.PartList);

  const reportsQuery = useGetSavedReports({
    filterModel: { items: [{ field: 'pageName', operator: 'equals', value: 'PartList' }] },
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, []);

  // Apply or reset the default report config when the query settles and
  // the user is on the default tab (no URL report or pinned report active,
  // and no codec chip state from a back-button remount).
  useEffect(() => {
    if (urlReportCode || activeReport || hasUrlChipState) return;
    const defaults = defaultReportQuery.data;
    if (defaults && defaults.length > 0) {
      const def = defaults[0];
      applyReportConfig(
        def.dataGridProperties?.filterModel ?? { items: [] },
        def.columnSelection?.length ? def.columnSelection : undefined,
        def.dataGridProperties?.columnOrder as Array<{ field: string }> | undefined
      );
      if (def.dataGridProperties?.filterModel) {
        setFilters(
          filterModelToFilterState(
            sanitizeSavedReportFilterModel(def.dataGridProperties.filterModel)
          )
        );
        setFilterBarKey((k) => k + 1);
      }
    } else if (defaults) {
      // Default was removed — reset filters to baseline and restore the default
      // view's own column layout (matching handleReportChange(null)).
      applyReportConfig(buildFilterModel(EMPTY_FILTERS));
      setFilters(EMPTY_FILTERS);
      setFilterBarKey((k) => k + 1);
      resetColumnsToDefault();
    }
  }, [defaultReportQuery.data]);

  const defaultTabLabel = useMemo(() => {
    const defaults = defaultReportQuery.data;
    if (defaults && defaults.length > 0) return defaults[0].name;
    return 'Default Parts';
  }, [defaultReportQuery.data]);

  const savedReports: SavedReport[] = useMemo(() => {
    if (!reportsQuery.data?.data) return [];
    return reportsQuery.data.data.map((r) => ({
      id: r.id,
      code: r.code,
      name: r.name,
      type: (r.savedReportAccess?.length > 0 ? 'shared' : 'my') as 'my' | 'shared',
      isDefault: r.isDefault,
    }));
  }, [reportsQuery.data]);

  const reportConfigQuery = useGetReportByCode(activeReport ?? '');

  const handleReportChange = useCallback(
    (code: string | null) => {
      setActiveReport(code);
      if (code === null) {
        // Re-apply the user's default report if they have one; otherwise
        // fall back to the system defaults (EMPTY_FILTERS).
        const userDefault = defaultReportQuery.data?.[0];
        if (userDefault?.dataGridProperties) {
          applyReportConfig(
            userDefault.dataGridProperties.filterModel,
            userDefault.columnSelection?.length ? userDefault.columnSelection : undefined,
            userDefault.dataGridProperties.columnOrder as Array<{ field: string }> | undefined
          );
          const derivedFilters = filterModelToFilterState(
            sanitizeSavedReportFilterModel(userDefault.dataGridProperties.filterModel)
          );
          setFilters(derivedFilters);
        } else {
          // No saved default report → the default view owns its own column
          // layout; restore it (session, else built defaults) so it doesn't
          // inherit the column order/visibility of the report tab just viewed.
          resetColumnsToDefault();
          setFilters(EMPTY_FILTERS);
          applyFilters(EMPTY_FILTERS);
        }
        setFilterBarKey((k) => k + 1);
      }
    },
    [
      applyFilters,
      applyReportConfig,
      setFilters,
      setFilterBarKey,
      defaultReportQuery.data,
      resetColumnsToDefault,
    ]
  );

  useEffect(() => {
    if (activeReport) reportConfigQuery.refetch();
  }, [activeReport]);

  useEffect(() => {
    if (reportConfigQuery.data && activeReport) {
      const { dataGridProperties, columnSelection } = reportConfigQuery.data;
      applyReportConfig(
        dataGridProperties.filterModel,
        columnSelection?.length ? columnSelection : undefined,
        dataGridProperties.columnOrder as Array<{ field: string }> | undefined
      );
      setFilters(
        filterModelToFilterState(sanitizeSavedReportFilterModel(dataGridProperties.filterModel))
      );
      setFilterBarKey((k) => k + 1);
    }
  }, [reportConfigQuery.data]);

  const activeReportName = useMemo(
    () => savedReports.find((r) => r.code === activeReport)?.name ?? null,
    [savedReports, activeReport]
  );

  const refreshAfterDefaultChange = useCallback(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, [reportsQuery, defaultReportQuery]);

  /**
   * Swap the isDefault flag: turn off the current default (if any) and
   * turn on the target report. Uses the bulk PATCH /defaultReport endpoint.
   */
  const setReportAsDefault = useCallback(
    async (targetId: number) => {
      const currentDefault = savedReports.find((r) => r.isDefault);
      const updates: Array<{ id: number; isDefault: boolean }> = [
        { id: targetId, isDefault: true },
      ];
      if (currentDefault && currentDefault.id !== targetId) {
        updates.unshift({ id: currentDefault.id, isDefault: false });
      }
      await bulkUpdateReports.mutateAsync(updates);
      refreshAfterDefaultChange();
    },
    [savedReports, bulkUpdateReports, refreshAfterDefaultChange]
  );

  const handleSaveNewReport = useCallback(
    async (name: string, setAsDefault: boolean) => {
      try {
        const result = await addNewReport.mutateAsync({
          name,
          code: generateUniqueCode(),
          reportTypeName: SavedReportTypeList.PartList,
          isDefault: setAsDefault,
          dataGridProperties: {
            filterModel,
            pageSize,
            sortModel: [{ field: sortModel.field, sort: sortModel.direction } as GridSortItem],
            columnOrder: columns.map((c) => ({ field: c.key })),
          },
          columnSelection: columnSelectionData,
        });
        if (setAsDefault && result?.id) {
          const currentDefault = savedReports.find((r) => r.isDefault);
          if (currentDefault) {
            await bulkUpdateReports.mutateAsync([
              { id: currentDefault.id, isDefault: false },
              { id: result.id, isDefault: true },
            ]);
          }
        }
        refreshAfterDefaultChange();
        toast.success('Report saved');
      } catch {
        toast.error('Failed to save report');
      }
    },
    [
      addNewReport,
      filterModel,
      pageSize,
      sortModel,
      columns,
      columnSelectionData,
      savedReports,
      bulkUpdateReports,
      refreshAfterDefaultChange,
    ]
  );

  const handleOverwriteReport = useCallback(
    async (setAsDefault: boolean) => {
      if (!activeReport) return;
      // savedReports comes from the paged useGetSavedReports (limit=100), so
      // a report deep-linked from outside that page won't appear there. Fall
      // back to the by-code fetch which already runs for activeReport.
      const fromList = savedReports.find((r) => r.code === activeReport);
      const fromFetch = reportConfigQuery.data;
      const target =
        fromList ??
        (fromFetch && fromFetch.code === activeReport
          ? { id: fromFetch.id, name: fromFetch.name, isDefault: fromFetch.isDefault }
          : undefined);
      if (!target) {
        toast.error('Report not found or not loaded');
        return;
      }
      try {
        // BFF treats /savedReport/{code} PUT as a full-record write — name
        // must be present or the report is rejected, so echo the existing
        // name and isDefault back in.
        await updateReportByCode(activeReport, {
          name: target.name,
          isDefault: target.isDefault,
          dataGridProperties: {
            filterModel,
            pageSize,
            sortModel: [{ field: sortModel.field, sort: sortModel.direction } as GridSortItem],
            columnOrder: columns.map((c) => ({ field: c.key })),
          },
          columnSelection: columnSelectionData,
        });
        if (setAsDefault && target.id != null) await setReportAsDefault(target.id);
        refreshAfterDefaultChange();
        toast.success('Report updated');
      } catch {
        toast.error('Failed to update report');
      }
    },
    [
      activeReport,
      filterModel,
      pageSize,
      sortModel,
      columns,
      columnSelectionData,
      savedReports,
      reportConfigQuery.data,
      setReportAsDefault,
      refreshAfterDefaultChange,
    ]
  );

  const handleSetDefault = useCallback(
    async (report: SavedReport) => {
      try {
        if (report.isDefault === false) {
          await bulkUpdateReports.mutateAsync([{ id: report.id, isDefault: false }]);
          refreshAfterDefaultChange();
          toast.success(`"${report.name}" removed as default`);
        } else {
          await setReportAsDefault(report.id);
          toast.success(`"${report.name}" set as default`);
        }
      } catch {
        toast.error('Failed to update default report');
      }
    },
    [setReportAsDefault, bulkUpdateReports, refreshAfterDefaultChange]
  );

  return {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
    setSaveDialogOpen,
  };
};
